import React from "react";
import { CatalogItemStripHeader } from "../../components/catalog/CatalogKindVisuals";
import {
  DATASET_FORMAT_LABEL,
  DatasetCatalogItem,
  datasetDisplayTitle,
  datasetSubtitle,
} from "../../services/datasetCatalog";
import { isFresh, metaLeft, relativeTime } from "./dataCatalogBrowseFormat";
import styles from "../catalog/CatalogBrowseLayout.module.css";

export interface DataCatalogBrowseCardProps {
  dataset: DatasetCatalogItem;
  selected: boolean;
  onSelect: () => void;
  onViewDetails: () => void;
  /** In the user's "all projects" list. Account-level, so it outranks the
   *  per-project state below it. */
  inAllProjects?: boolean;
  /** Right-click. The grid owns the menu, so the card only reports the event
   *  and selects itself - the same thing a left-click does. */
  onContextMenu?: (e: React.MouseEvent) => void;
}

export function DataCatalogBrowseCard({
  dataset,
  selected,
  onSelect,
  onViewDetails,
  onContextMenu,
  inAllProjects = false,
}: DataCatalogBrowseCardProps) {
  const fresh = isFresh(dataset.updatedAt);
  const left = metaLeft(dataset);
  const tags = dataset.tags.length > 0 ? dataset.tags.slice(0, 3) : [dataset.format];

  return (
    <article
      className={[
        styles.card,
        selected ? styles.cardActive : "",
        styles[`card_${dataset.format}`] || "",
      ].join(" ")}
      // The cross-surface identity attribute the drawer card and the tools
      // palette already carry, and which test_frontend/README.md says every
      // card root has. This one did not, so nothing could address a browse
      // card except by its display copy.
      data-dataset-id={dataset.id}
      role="button"
      tabIndex={0}
      onContextMenu={onContextMenu}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect();
      }}
    >
      <div className={`${styles.cardStrip} ${styles[`strip_${dataset.format}`] || ""}`}>
        <CatalogItemStripHeader
          kind="dataset"
          badge={
            <span className={styles.cardFormatBadge}>{DATASET_FORMAT_LABEL[dataset.format]}</span>
          }
        />
      </div>

      <div className={styles.cardBody}>
        <h2 className={styles.cardTitle}>
          {datasetDisplayTitle(dataset)}
        </h2>
        <p className={styles.publisher}>
          {datasetSubtitle(dataset)}
          {/*{formatDatasetLocation(dataset)} · v1.0.0*/}
        </p>
        <p
          className={styles.cardDescription}
          {...(!dataset.description ? { "aria-hidden": true } : {})}
        >
          {dataset.description || "\u00a0"}
        </p>
        {/* The subject of the `catalog-tag-chips-are-plain` baseline (#333).
            CSS-module class names are hashed, so a capture cannot be aimed at
            `.tagRow`; this attribute is what one selector can find on all
            three catalog pages. */}
        <div className={styles.tagRow} data-curio-tag-row="true">
          {tags.map((tag) => (
            <span key={tag} className={styles.tag} data-curio-tag-chip="true">
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.cardMeta}>
        <span className={styles.metaLeft}>{left}</span>
        <span className={styles.metaRight}>
          <span
            className={`${styles.liveDot} ${fresh ? styles.liveDotGreen : styles.liveDotGray}`}
          />
          <span>{relativeTime(dataset.updatedAt)}</span>
        </span>
      </div>

      <div className={styles.cardActions}>
        {/* A card states what a thing IS and offers the one way in. Publishing
            is an account-level decision about a specific item, so it lives in
            the detail drawer with the other decisions - not on every tile in a
            grid, where it competed with the card's own identity and put a
            deployment-wide write one stray click from a browse gesture. */}
        <div className={styles.cardActionsLeft}>
          {/* Two different scopes, and the wider one wins: a dataset in every
              project is also in this one, so showing both would be a tautology
              plus a narrowing. Matches the Node card, which shows only
              "In all projects". */}
          {inAllProjects ? (
            <span className={styles.cardStatus}>✓ In all projects</span>
          ) : dataset.installed ? (
            <span className={styles.cardStatus}>✓ In project</span>
          ) : null}
        </div>
        <div className={styles.cardActionsRight}>
          <button
            className={styles.linkButton}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
          >
            View details
          </button>
        </div>
      </div>
    </article>
  );
}
